import React, { useEffect, useState } from "react";
import { Button, InputNumber, Modal, Switch } from "antd";
import { RotateCw } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { Note } from "../ssh-proxy/sshProxyUi";
import { passwordErrorText, rotatePasswordNow, setAccountRotation } from "../../../service/sshProxyService";

// Password rotation for one account on one endpoint: on/off and "every N
// days". Used from Endpoint details (Local users) and Endpoint Users.
//
//   <AccountRotationModal open instanceId={7} account="alice" hostname="web-01"
//     rotation={{ enabled, intervalDays, own, profile }} defaultIntervalDays={30}
//     onClose={...} onSaved={(rotation) => ...} onRotated={() => ...} />
//
// When rotation is already on, "Rotate now" (a new password at once) is here
// too, so the row menu needs one item for both.
//
// On rotates once at once, then on the schedule; off stops rotating, and the
// last password stays in the vault, revealable. An account still on a shared
// profile (own === false, or a profile named) can be saved as-is: that gives
// it its own schedule.

export default function AccountRotationModal({ open, instanceId, account, hostname, rotation, defaultIntervalDays = 30, onClose, onSaved, onRotated }) {
  const [rotatingNow, setRotatingNow] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(defaultIntervalDays);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEnabled(!!rotation?.enabled);
    setDays(rotation?.intervalDays || defaultIntervalDays || 30);
    setError("");
    setSaving(false);
  }, [open, rotation, defaultIntervalDays]);

  const wasOn = !!rotation?.enabled;

  const rotateNow = () => {
    setRotatingNow(true);
    setError("");
    rotatePasswordNow({
      endpointId: Number(instanceId),
      account,
      onSuccess: () => {
        setRotatingNow(false);
        onRotated && onRotated();
        onClose && onClose();
      },
      onFailure: (err) => {
        setRotatingNow(false);
        setError(passwordErrorText(err, "Couldn't queue the rotation."));
      },
    });
  };
  const daysOk = Number.isInteger(days) && days >= 1 && days <= 365;
  const onShared = wasOn && (rotation?.own === false || !!rotation?.profile);
  const changed = enabled !== wasOn || (enabled && days !== (rotation?.intervalDays || defaultIntervalDays)) || (enabled && onShared);

  const save = () => {
    if (enabled && !daysOk) return;
    setSaving(true);
    setError("");
    setAccountRotation({
      instanceId: Number(instanceId),
      account,
      enabled,
      intervalDays: enabled ? days : rotation?.intervalDays || defaultIntervalDays || 30,
      onSuccess: (res) => {
        setSaving(false);
        onSaved && onSaved(res?.rotation || { enabled, intervalDays: days });
      },
      onFailure: (err) => {
        setSaving(false);
        setError(passwordErrorText(err, "Couldn't change password rotation."));
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      maskClosable={!saving}
      destroyOnClose
      width={480}
      title={
        <span style={{ fontFamily: AN_FONT }}>
          Password rotation · <span style={{ fontFamily: AN_MONO, fontWeight: 600 }}>{account}</span>
          {hostname ? <span style={{ color: AN_COLORS.muted, fontWeight: 500 }}> on {hostname}</span> : null}
        </span>
      }
      footer={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {wasOn && (
            <Button
              icon={<RotateCw size={13} />}
              onClick={rotateNow}
              loading={rotatingNow}
              disabled={saving || !!rotation?.jobRunning}
            >
              {rotation?.jobRunning ? "Rotating…" : "Rotate now"}
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving || rotatingNow}>Cancel</Button>
          <Button type="primary" onClick={save} loading={saving} disabled={rotatingNow || !changed || (enabled && !daysOk)}>
            Save
          </Button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 16, fontFamily: AN_FONT, paddingTop: 4 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
          <Switch checked={enabled} onChange={setEnabled} disabled={saving} style={{ marginTop: 2 }} />
          <span>
            <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: AN_COLORS.ink }}>Rotate this password</span>
            <span style={{ display: "block", fontSize: 12.5, color: AN_COLORS.muted, marginTop: 2, lineHeight: 1.45 }}>
              Authnull sets a new random password and keeps it in the vault. Nobody needs to know it.
            </span>
          </span>
        </label>

        {enabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: 56, fontSize: 13.5, color: AN_COLORS.ink2 }}>
            Rotate every
            <InputNumber
              min={1}
              max={365}
              precision={0}
              value={days}
              onChange={(v) => setDays(typeof v === "number" ? v : NaN)}
              disabled={saving}
              status={daysOk ? undefined : "error"}
              style={{ width: 84 }}
            />
            days
          </div>
        )}
        {enabled && !daysOk && <div style={{ paddingLeft: 56, fontSize: 12, color: AN_COLORS.danger }}>Choose 1 to 365 days.</div>}

        {enabled && !wasOn && <Note tone="info">It rotates once now, then every {daysOk ? days : "N"} days.</Note>}
        {enabled && onShared && (
          <Note tone="info">
            This account follows the shared profile{rotation?.profile ? ` “${rotation.profile}”` : ""}. Saving gives it its own schedule.
          </Note>
        )}
        {!enabled && wasOn && (
          <Note tone="warn">Rotation stops. The last password stays in the vault and can still be revealed.</Note>
        )}
        {error && <Note tone="danger">{error}</Note>}
      </div>
    </Modal>
  );
}
